<script setup lang="ts">
import { TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import type { JournalTabItem } from '../lib/journal.const';

const props = defineProps<{
  items: JournalTabItem[];
  active: string;
}>();

// Every tab has an address, so a click is a navigation, not local state
const handleChange = (key: string | number) => {
  const item = props.items.find((tab) => tab.key === String(key));

  if (item) navigateTo(item.to);
};
</script>

<template>
  <TabsRoot
    :model-value="active"
    :class="$style.root"
    @update:model-value="handleChange"
  >
    <TabsList aria-label="Разделы журналов" :class="$style.list">
      <TabsTrigger
        v-for="tab in items"
        :key="tab.key"
        as="div"
        :value="tab.key"
        :class="$style.tab"
      >
        {{ tab.label }}
      </TabsTrigger>
    </TabsList>
  </TabsRoot>
</template>

<style module lang="scss">
// Full width, otherwise the row is as wide as the 531px list below and the
// max-width of that list has nothing to shrink against on a narrow screen
.root {
  display: flex;
  justify-content: center;
  width: 100%;
}

// The editors add and remove tabs, so the columns follow their count. The
// layout's 531px holds the four it was drawn with; more tabs widen the row, and
// past the screen width it scrolls instead of squeezing the labels
.list {
  display: grid;
  grid-auto-columns: minmax(max-content, 1fr);
  grid-auto-flow: column;
  min-width: min(531px, 100%);
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid var(--color-gray-line);
}

.tab {
  @include text_body2_medium;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 var(--space-1x);
  color: var(--color-gray-text);
  background-color: var(--color-gray-bg);
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.2s,
    background-color 0.2s;

  &:not(:last-child) {
    border-right: 1px solid var(--color-gray-line);
  }

  &:hover {
    color: var(--color-black-1);
  }

  &[data-state='active'] {
    color: var(--color-black-1);
    background-color: var(--color-white);
  }
}
</style>
